import { motion } from "framer-motion";

/**
 * Stylised SVG Panda mascot with mood states.
 * mood: "waiting" | "excited" | "celebrating" | "waving"
 * size: pixel size
 */
export default function PandaMascot({ mood = "waiting", size = 160 }) {
  const bob = mood === "excited"
    ? { y: [0, -8, 0], scale: [1, 1.05, 1], transition: { duration: 0.4, repeat: Infinity } }
    : mood === "celebrating"
      ? { y: [0, -18, 0], rotate: [0, -6, 6, 0], transition: { duration: 0.8, repeat: Infinity } }
      : { y: [0, -4, 0], transition: { duration: 2.4, repeat: Infinity, ease: "easeInOut" } };

  return (
    <motion.div style={{ width: size, height: size }} animate={bob} className="relative pointer-events-none select-none" aria-hidden>
      <svg viewBox="0 0 200 200" width={size} height={size} className="drop-shadow-xl">
        <defs>
          <radialGradient id="pFace" cx="50%" cy="45%" r="55%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#F1F1F1" />
          </radialGradient>
          <radialGradient id="pEar" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#3a3a3a" />
            <stop offset="100%" stopColor="#111" />
          </radialGradient>
          <radialGradient id="pCheek" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#ffb0b0" />
            <stop offset="100%" stopColor="#ff8a95" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Ears */}
        <circle cx="45" cy="46" r="26" fill="url(#pEar)" />
        <circle cx="155" cy="46" r="26" fill="url(#pEar)" />

        {/* Face */}
        <ellipse cx="100" cy="110" rx="80" ry="72" fill="url(#pFace)" stroke="#2B2B2B" strokeWidth="3" />

        {/* Eye patches */}
        <ellipse cx="72" cy="102" rx="18" ry="22" fill="#111" transform="rotate(-10 72 102)" />
        <ellipse cx="128" cy="102" rx="18" ry="22" fill="#111" transform="rotate(10 128 102)" />

        {/* Cheeks */}
        <circle cx="56" cy="132" r="14" fill="url(#pCheek)" />
        <circle cx="144" cy="132" r="14" fill="url(#pCheek)" />

        {/* Eyes — expression varies by mood */}
        <PandaEyes mood={mood} />

        {/* Nose */}
        <ellipse cx="100" cy="128" rx="7" ry="5" fill="#111" />

        {/* Mouth — expression varies */}
        <PandaMouth mood={mood} />
      </svg>

      {/* Waving hand overlay (celebrating/waving) */}
      {(mood === "waving" || mood === "celebrating") && (
        <motion.div
          className="absolute"
          style={{ right: -size * 0.12, top: size * 0.32, width: size * 0.35, height: size * 0.35, transformOrigin: "20% 90%" }}
          animate={{ rotate: [-15, 20, -15] }}
          transition={{ duration: 0.9, repeat: Infinity, ease: "easeInOut" }}
        >
          <svg viewBox="0 0 60 60" width="100%" height="100%">
            <ellipse cx="30" cy="30" rx="18" ry="22" fill="#111" />
            <ellipse cx="30" cy="30" rx="12" ry="15" fill="#fff" />
          </svg>
        </motion.div>
      )}

      {/* Confetti popsicles for celebrating */}
      {mood === "celebrating" && (
        <>
          {[..."🍭🍓🪙✨🍫"].map((emoji, i) => (
            <motion.span
              key={i}
              className="absolute text-2xl"
              style={{ left: 20 + i * 30, top: -10 }}
              initial={{ y: 0, opacity: 0, scale: 0.6, rotate: 0 }}
              animate={{ y: [-30, -60, -40], opacity: [0, 1, 0], scale: [0.6, 1, 0.8], rotate: [0, 180, 360] }}
              transition={{ duration: 1.4, delay: i * 0.15, repeat: Infinity, repeatDelay: 0.3 }}
            >
              {emoji}
            </motion.span>
          ))}
        </>
      )}
    </motion.div>
  );
}

function PandaEyes({ mood }) {
  // waiting: open eyes; excited: sparkle eyes; celebrating: closed happy eyes; waving: friendly eyes
  if (mood === "celebrating") {
    return (
      <>
        <path d="M 65 100 Q 72 92 79 100" stroke="#fff" strokeWidth="3.5" fill="none" strokeLinecap="round" />
        <path d="M 121 100 Q 128 92 135 100" stroke="#fff" strokeWidth="3.5" fill="none" strokeLinecap="round" />
      </>
    );
  }
  if (mood === "excited") {
    return (
      <>
        <circle cx="72" cy="102" r="6" fill="#fff" />
        <circle cx="75" cy="99" r="1.6" fill="#111" />
        <circle cx="128" cy="102" r="6" fill="#fff" />
        <circle cx="131" cy="99" r="1.6" fill="#111" />
        {/* Sparkle */}
        <text x="60" y="80" fontSize="12" fill="#FFC93C">✦</text>
        <text x="130" y="80" fontSize="12" fill="#FFC93C">✦</text>
      </>
    );
  }
  // waiting / waving default
  return (
    <>
      <circle cx="72" cy="102" r="5" fill="#fff" />
      <circle cx="128" cy="102" r="5" fill="#fff" />
    </>
  );
}

function PandaMouth({ mood }) {
  if (mood === "celebrating") {
    // Open smiley O mouth
    return <path d="M 88 148 Q 100 168 112 148 Q 100 155 88 148 Z" fill="#B71C1C" stroke="#2B2B2B" strokeWidth="2" strokeLinejoin="round" />;
  }
  if (mood === "excited") {
    // Big open smile
    return <path d="M 84 145 Q 100 165 116 145" stroke="#2B2B2B" strokeWidth="3.5" fill="none" strokeLinecap="round" />;
  }
  if (mood === "waving") {
    return <path d="M 86 143 Q 100 155 114 143" stroke="#2B2B2B" strokeWidth="3" fill="none" strokeLinecap="round" />;
  }
  // waiting — soft neutral smile
  return <path d="M 90 142 Q 100 150 110 142" stroke="#2B2B2B" strokeWidth="3" fill="none" strokeLinecap="round" />;
}
